
import React, { useState, useEffect } from 'react';
import type { ApiSettings, MailchimpSettings } from '../types';
import { useLocalization } from '../hooks/useLocalization';
import { getAuthToken } from '../services/tourvisioService';
import { getMailchimpSettings, saveMailchimpSettings } from '../services/mailchimpService';

interface SettingsPanelProps {
    onClose: () => void;
}

type TestStatus = 'idle' | 'testing' | 'success' | 'error';
type Tab = 'general' | 'integrations' | 'advanced';

export const SettingsPanel: React.FC<SettingsPanelProps> = ({ onClose }) => {
    const { t } = useLocalization();
    const [activeTab, setActiveTab] = useState<Tab>('general');

    // General Settings (Local Storage for now)
    const [siteSettings, setSiteSettings] = useState({
        siteName: 'Lumi Tours',
        contactEmail: 'info@lumitours.com',
        phoneNumber: '+46 8 123 45 67',
        address: 'Stockholm, Sweden',
        socialFacebook: 'https://facebook.com/lumitours',
        socialInstagram: 'https://instagram.com/lumitours',
        socialLinkedin: '',
        features: {
            contents: true,
            pageBuilder: false,
            localization: true,
            flashSale: true,
            ctaCards: true,
            headerFooter: true,
            menuSettings: true,
            analytics: false,
            cookieStats: false
        }
    });

    // Tourvisio Settings
    const [settings, setSettings] = useState<ApiSettings>({
        serviceAddress: 'https://b2cservice.lumitours.com',
        agency: 'lumiweb',
        user: 'lumiwebus',
        password: '1',
        activeProviders: []
    });

    // Mailchimp Settings
    const [mcSettings, setMcSettings] = useState<MailchimpSettings>({
        apiKey: '',
        serverPrefix: '',
        audienceId: '',
        isActive: false
    });

    // Server API Keys
    const [serverKeys, setServerKeys] = useState<Record<string, string>>({});

    const [isSaved, setIsSaved] = useState(false);
    const [testStatus, setTestStatus] = useState<TestStatus>('idle');
    const [testResponse, setTestResponse] = useState('');
    const [newProvider, setNewProvider] = useState('');

    useEffect(() => {
        // Load Tourvisio
        const savedSettings = localStorage.getItem('tourvisioApiSettings');
        if (savedSettings) {
            setSettings(JSON.parse(savedSettings));
        }
        // Load Mailchimp
        setMcSettings(getMailchimpSettings());

        // Load Site Settings
        const savedSiteSettings = localStorage.getItem('siteSettings');
        if (savedSiteSettings) {
            setSiteSettings(JSON.parse(savedSiteSettings));
        }

        // Load Server Keys
        fetch('/api/settings/api-keys')
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setServerKeys(data.keys);
                }
            })
            .catch(err => console.error('Failed to load server keys', err));
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSettings({
            ...settings,
            [e.target.name]: e.target.value,
        });
    };

    const handleMcChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setMcSettings({
            ...mcSettings,
            [e.target.name]: value
        });
    };

    const handleSiteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.name.startsWith('feature_')) {
            const featureName = e.target.name.replace('feature_', '');
            setSiteSettings(prev => ({
                ...prev,
                features: {
                    ...(prev.features || {}),
                    [featureName]: e.target.checked
                }
            }));
        } else {
            setSiteSettings(prev => ({
                ...prev,
                [e.target.name]: e.target.value
            }));
        }
    };

    const handleServerKeyChange = (key: string, value: string) => {
        setServerKeys(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleAddProvider = () => {
        if (newProvider.trim() && !settings.activeProviders?.includes(newProvider.trim())) {
            setSettings({
                ...settings,
                activeProviders: [...(settings.activeProviders || []), newProvider.trim()]
            });
            setNewProvider('');
        }
    };

    const handleRemoveProvider = (provider: string) => {
        setSettings({
            ...settings,
            activeProviders: (settings.activeProviders || []).filter(p => p !== provider)
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Save Tourvisio
        const sanitizedAddress = settings.serviceAddress.replace(/\/+$/, "");
        const finalSettings = { ...settings, serviceAddress: sanitizedAddress };
        localStorage.setItem('tourvisioApiSettings', JSON.stringify(finalSettings));

        // Save Mailchimp
        saveMailchimpSettings(mcSettings);

        // Save Site Settings
        localStorage.setItem('siteSettings', JSON.stringify(siteSettings));

        // Save Server Keys
        try {
            await fetch('/api/settings/api-keys', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ keys: serverKeys })
            });
        } catch (err) {
            console.error('Failed to save server keys', err);
        }

        alert(t('settings_saved'));
        setIsSaved(true);
        onClose();
    };

    const dummyLog = () => { };

    const handleTestApi = async () => {
        setTestStatus('testing');
        setTestResponse('');
        const sanitizedAddress = settings.serviceAddress.replace(/\/+$/, "");
        const currentSettings = { ...settings, serviceAddress: sanitizedAddress };

        try {
            setTestResponse('Authenticating...');
            const token = await getAuthToken(currentSettings, dummyLog);
            if (!token) throw new Error("Failed to retrieve token.");
            setTestResponse('Authentication Successful. Token retrieved.');
            setTestStatus('success');
        } catch (error) {
            setTestStatus('error');
            setTestResponse(error instanceof Error ? error.message : String(error));
        }
    };

    return (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="max-w-4xl mx-auto bg-white dark:bg-slate-800 rounded-2xl shadow-xl overflow-hidden">

                {/* Header */}
                <div className="px-8 py-6 border-b border-gray-200 dark:border-slate-700 flex justify-between items-center bg-gray-50 dark:bg-slate-900">
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-white">{t('settings') || 'Settings'}</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                        <span className="material-icons-round">close</span>
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                    <button
                        onClick={() => setActiveTab('general')}
                        className={`flex-1 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'general' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                        General
                    </button>
                    <button
                        onClick={() => setActiveTab('integrations')}
                        className={`flex-1 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'integrations' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                        Integrations
                    </button>
                    <button
                        onClick={() => setActiveTab('advanced')}
                        className={`flex-1 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'advanced' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                        Server Configuration
                    </button>
                </div>

                <div className="p-8">
                    <form onSubmit={handleSubmit} className="space-y-8">

                        {/* GENERAL TAB */}
                        {activeTab === 'general' && (
                            <div className="space-y-6 animate-fade-in">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Site Name</label>
                                        <input type="text" name="siteName" value={siteSettings.siteName} onChange={handleSiteChange} className="mt-1 p-2 w-full border border-gray-300 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Contact Email</label>
                                        <input type="email" name="contactEmail" value={siteSettings.contactEmail} onChange={handleSiteChange} className="mt-1 p-2 w-full border border-gray-300 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number</label>
                                        <input type="text" name="phoneNumber" value={siteSettings.phoneNumber} onChange={handleSiteChange} className="mt-1 p-2 w-full border border-gray-300 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Address</label>
                                        <input type="text" name="address" value={siteSettings.address} onChange={handleSiteChange} className="mt-1 p-2 w-full border border-gray-300 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                                    </div>
                                </div>

                                <div className="pt-6 border-t border-gray-200 dark:border-slate-700">
                                    <h3 className="font-bold text-gray-800 dark:text-white mb-4">Social Media Links</h3>
                                    <div className="grid grid-cols-1 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                                <span className="material-icons-round text-blue-600">facebook</span> Facebook
                                            </label>
                                            <input type="text" name="socialFacebook" value={siteSettings.socialFacebook} onChange={handleSiteChange} className="mt-1 p-2 w-full border border-gray-300 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white" placeholder="https://facebook.com/..." />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                                <span className="material-icons-round text-pink-600">camera_alt</span> Instagram
                                            </label>
                                            <input type="text" name="socialInstagram" value={siteSettings.socialInstagram} onChange={handleSiteChange} className="mt-1 p-2 w-full border border-gray-300 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white" placeholder="https://instagram.com/..." />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                                <span className="material-icons-round text-blue-500">work</span> LinkedIn
                                            </label>
                                            <input type="text" name="socialLinkedin" value={siteSettings.socialLinkedin} onChange={handleSiteChange} className="mt-1 p-2 w-full border border-gray-300 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white" placeholder="https://linkedin.com/in/..." />
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-6 border-t border-gray-200 dark:border-slate-700">
                                    <h3 className="font-bold text-gray-800 dark:text-white mb-4">Content Module Features</h3>
                                    <p className="text-sm text-gray-500 mb-4">Enable or disable content management modules. Inactive modules will be hidden from the menu to keep the interface clean.</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {[
                                            { id: 'contents', label: 'Static Contents' },
                                            { id: 'pageBuilder', label: 'Page Builder' },
                                            { id: 'localization', label: 'Localization' },
                                            { id: 'flashSale', label: 'Flash Sale' },
                                            { id: 'ctaCards', label: 'CTA Cards' },
                                            { id: 'headerFooter', label: 'Header & Footer Editor' },
                                            { id: 'menuSettings', label: 'Menu Settings' },
                                            { id: 'analytics', label: 'Analytics Dashboard' },
                                            { id: 'cookieStats', label: 'Cookie Stats' }
                                        ].map(feature => (
                                            <div key={feature.id} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 dark:hover:bg-slate-700/50">
                                                <input
                                                    type="checkbox"
                                                    id={`feature_${feature.id}`}
                                                    name={`feature_${feature.id}`}
                                                    checked={siteSettings.features?.[feature.id as keyof typeof siteSettings.features] ?? false}
                                                    onChange={handleSiteChange}
                                                    className="w-4 h-4 text-primary rounded focus:ring-primary"
                                                />
                                                <label htmlFor={`feature_${feature.id}`} className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer select-none">
                                                    {feature.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* INTEGRATIONS TAB */}
                        {activeTab === 'integrations' && (
                            <div className="space-y-8 animate-fade-in">
                                {/* Tourvisio Section */}
                                <div className="bg-gray-50 dark:bg-slate-700/50 p-6 rounded-xl border border-gray-200 dark:border-slate-600">
                                    <h3 className="font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                                        <span className="material-icons-round text-teal-500">api</span> Tourvisio API
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Service Address</label>
                                            <input type="text" name="serviceAddress" value={settings.serviceAddress} onChange={handleChange} className="mt-1 p-2 w-full border border-gray-300 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white" required />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Agency Code</label>
                                                <input type="text" name="agency" value={settings.agency} onChange={handleChange} className="mt-1 p-2 w-full border border-gray-300 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white" required />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">User</label>
                                                <input type="text" name="user" value={settings.user} onChange={handleChange} className="mt-1 p-2 w-full border border-gray-300 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white" required />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
                                            <input type="password" name="password" value={settings.password} onChange={handleChange} className="mt-1 p-2 w-full border border-gray-300 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white" required />
                                        </div>

                                        <div className="pt-4">
                                            <div className="flex justify-between items-center mb-2">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Active Providers</label>
                                                <div className="flex gap-2">
                                                    <input type="text" value={newProvider} onChange={(e) => setNewProvider(e.target.value)} placeholder="e.g. SANTSG" className="w-32 border border-gray-300 rounded-lg px-2 text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                                                    <button type="button" onClick={handleAddProvider} className="bg-primary text-white px-3 py-1 rounded-lg text-sm hover:bg-primary/90">Add</button>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {settings.activeProviders && settings.activeProviders.length > 0 ? (
                                                    settings.activeProviders.map(p => (
                                                        <span key={p} className="inline-flex items-center bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 text-xs font-bold px-3 py-1 rounded-full">
                                                            {p} <button type="button" onClick={() => handleRemoveProvider(p)} className="ml-2 hover:text-blue-900 dark:hover:text-blue-100 font-bold">×</button>
                                                        </span>
                                                    ))
                                                ) : <span className="text-xs text-gray-400 italic">All providers included</span>}
                                            </div>
                                        </div>

                                        <div className="pt-4 border-t border-gray-200 dark:border-slate-600">
                                            <div className="flex items-center justify-between">
                                                <button type="button" onClick={handleTestApi} disabled={testStatus === 'testing'} className="text-sm text-primary hover:text-primary/80 font-medium">
                                                    {testStatus === 'testing' ? 'Connecting...' : 'Test Connection'}
                                                </button>
                                                {testResponse && (
                                                    <span className={`text-xs px-2 py-1 rounded ${testStatus === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                        {testStatus === 'success' ? 'Connected' : 'Error'}
                                                    </span>
                                                )}
                                            </div>
                                            {testResponse && testStatus === 'error' && (
                                                <p className="mt-2 text-xs text-red-500 font-mono">{testResponse}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Mailchimp Section */}
                                <div className="bg-gray-50 dark:bg-slate-700/50 p-6 rounded-xl border border-gray-200 dark:border-slate-600">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="font-bold text-gray-800 dark:text-white flex items-center gap-2">
                                            <span className="material-icons-round text-yellow-500">mail</span> Mailchimp
                                        </h3>
                                        <div className="flex items-center gap-2">
                                            <input type="checkbox" id="isActive" name="isActive" checked={mcSettings.isActive} onChange={handleMcChange} className="w-5 h-5 rounded text-primary focus:ring-primary" />
                                            <label htmlFor="isActive" className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable</label>
                                        </div>
                                    </div>

                                    {mcSettings.isActive && (
                                        <div className="space-y-4 animate-fade-in">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">API Key</label>
                                                <input type="password" name="apiKey" value={mcSettings.apiKey} onChange={handleMcChange} className="mt-1 p-2 w-full border border-gray-300 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white" placeholder="md-xxxxxxxx..." />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Server Prefix</label>
                                                    <input type="text" name="serverPrefix" value={mcSettings.serverPrefix} onChange={handleMcChange} className="mt-1 p-2 w-full border border-gray-300 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white" placeholder="e.g. us6" />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Audience ID</label>
                                                    <input type="text" name="audienceId" value={mcSettings.audienceId} onChange={handleMcChange} className="mt-1 p-2 w-full border border-gray-300 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* SERVER CONFIG TAB */}
                        {activeTab === 'advanced' && (
                            <div className="space-y-6 animate-fade-in">
                                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-xl border border-yellow-200 dark:border-yellow-800 mb-6">
                                    <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-400 font-bold mb-1">
                                        <span className="material-icons-round">warning</span>
                                        Warning
                                    </div>
                                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                                        These settings affect the server-side configuration. Changes might require a server restart to take full effect.
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    {['GOOGLE_MAPS_API_KEY', 'GOOGLE_PLACES_API_KEY', 'GEMINI_API_KEY', 'OPENAI_API_KEY', 'APIFY_API_TOKEN'].map(key => (
                                        <div key={key}>
                                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">{key.replace(/_/g, ' ')}</label>
                                            <input
                                                type="text"
                                                value={serverKeys[key] || ''}
                                                onChange={(e) => handleServerKeyChange(key, e.target.value)}
                                                className="p-2 w-full border border-gray-300 rounded-lg font-mono text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                                                placeholder="Not configured"
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-8 border-t border-gray-200 dark:border-slate-700 pt-6">
                                    <h3 className="font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                                        <span className="material-icons-round text-blue-500">folder</span> FTP Configuration
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">FTP Host</label>
                                                <input
                                                    type="text"
                                                    value={serverKeys['FTP_HOST'] || ''}
                                                    onChange={(e) => handleServerKeyChange('FTP_HOST', e.target.value)}
                                                    className="mt-1 p-2 w-full border border-gray-300 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                                                    placeholder="ftp.example.com"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">FTP User</label>
                                                <input
                                                    type="text"
                                                    value={serverKeys['FTP_USER'] || ''}
                                                    onChange={(e) => handleServerKeyChange('FTP_USER', e.target.value)}
                                                    className="mt-1 p-2 w-full border border-gray-300 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                                                    placeholder="username"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">FTP Password</label>
                                                <input
                                                    type="password"
                                                    value={serverKeys['FTP_PASS'] || ''}
                                                    onChange={(e) => handleServerKeyChange('FTP_PASS', e.target.value)}
                                                    className="mt-1 p-2 w-full border border-gray-300 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                                                    placeholder="••••••••"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">FTP Root Path</label>
                                                <input
                                                    type="text"
                                                    value={serverKeys['FTP_ROOT'] || ''}
                                                    onChange={(e) => handleServerKeyChange('FTP_ROOT', e.target.value)}
                                                    className="mt-1 p-2 w-full border border-gray-300 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                                                    placeholder="/httpdocs"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id="ftpSecure"
                                                checked={serverKeys['FTP_SECURE'] === 'true'}
                                                onChange={(e) => handleServerKeyChange('FTP_SECURE', String(e.target.checked))}
                                                className="w-5 h-5 rounded text-primary focus:ring-primary"
                                            />
                                            <label htmlFor="ftpSecure" className="text-sm font-medium text-gray-700 dark:text-gray-300">Secure (FTPS)</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-slate-700">
                            <button type="button" onClick={onClose} className="px-6 py-2.5 border border-gray-300 rounded-xl text-sm font-bold text-gray-700 bg-white hover:bg-gray-50 dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:hover:bg-slate-600 transition-all">
                                {t('cancel')}
                            </button>
                            <button type="submit" className="px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 transition-all">
                                {t('save_settings')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
